import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type PaymentState =
  | 'initiated'
  | 'awaiting_response'
  | 'approved'
  | 'declined'
  | 'indeterminate'
  | 'voided'
  | 'reversed'
  | 'failed';

export type PaymentMethod = 'card' | 'cash' | 'voucher' | 'split';

export type PaymentAttemptSummary = {
  id: string;
  attemptId: string;
  shiftId: string;
  terminalId: string;
  cashierId: string;
  method: PaymentMethod;
  amountMinor: string;
  currencyCode: string;
  state: PaymentState;
  vendorAttemptRef: string | null;
  vendorResponseCode: string | null;
  reasonMessage: string | null;
  initiatedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PaymentAttemptDetail = PaymentAttemptSummary & {
  reversal: null | {
    id: string;
    reasonCode: string;
    reasonMessage: string | null;
    authoredAt: string;
    pulledAt: string | null;
    completedAt: string | null;
  };
};

export type PaymentListResponse = {
  attempts: PaymentAttemptSummary[];
  page: number;
  pageSize: number;
  total: number;
};

export type PaymentListQuery = {
  page?: number;
  pageSize?: number;
  state?: PaymentState;
  stuckOnly?: boolean;
};

const LIST_KEY = ['payments'] as const;
const ONE_KEY = (id: string) => ['payments', id] as const;

export function usePayments(query: PaymentListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<PaymentListResponse> => {
      const params: Record<string, string | number | boolean> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.state !== undefined) params['filter[state]'] = query.state;
      if (query.stuckOnly === true) params['filter[stuckOnly]'] = 'true';
      const res = await apiClient.get<PaymentListResponse>('/payments/reconciliation', { params });
      return res.data;
    },
  });
}

export function usePaymentAttempt(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['payments', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<PaymentAttemptDetail> => {
      const res = await apiClient.get<PaymentAttemptDetail>(`/payments/attempts/${id}`);
      return res.data;
    },
  });
}

export type ReconcileAction = 'mark_voided' | 'mark_approved' | 'issue_reversal';

export function useReconcilePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      action: ReconcileAction;
      reason: string;
    }): Promise<PaymentAttemptDetail> => {
      const res = await apiClient.post<PaymentAttemptDetail>(
        `/payments/attempts/${vars.id}/reconcile`,
        { action: vars.action, reason: vars.reason },
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

/** Re-use the shifts formatter for consistency. */
export { formatMinor } from './use-shifts';
